import { QueryClient } from '@tanstack/react-query';

/**
 * Query configuration.
 *
 * `staleTime` is 30 seconds rather than zero: an HR administrator moving
 * between the directory and an employee record twice in ten seconds should not
 * re-fetch the directory, and the demonstration data does not change under
 * them. Mutations invalidate explicitly instead.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

/**
 * Query options for data other people change under the reader — a payroll
 * period another role approves. Fresh on every visit and whenever the window
 * regains focus, instead of the 30-second cache above.
 */
export const LIVE_QUERY = {
  staleTime: 0,
  refetchOnMount: 'always',
  refetchOnWindowFocus: 'always',
} as const;

/**
 * Query keys.
 *
 * Centralised so an invalidation cannot miss a list because one screen spelled
 * its key differently. Every key starts with its module, so a module-wide
 * invalidation is a prefix match.
 */
export const queryKeys = {
  dashboard: {
    workforce: () => ['dashboard', 'workforce'] as const,
    attention: () => ['dashboard', 'attention'] as const,
    attendanceToday: () => ['dashboard', 'attendance-today'] as const,
    movement: () => ['dashboard', 'movement'] as const,
    events: () => ['dashboard', 'events'] as const,
    activity: () => ['dashboard', 'activity'] as const,
  },
  employees: {
    all: () => ['employees'] as const,
    list: (filter: unknown) => ['employees', 'list', filter] as const,
    one: (id: string) => ['employees', 'one', id] as const,
    summary: () => ['employees', 'summary'] as const,
    attendance: (id: string) => ['employees', 'attendance', id] as const,
  },
  organization: {
    departments: () => ['organization', 'departments'] as const,
    departmentOverview: () => ['organization', 'department-overview'] as const,
    teams: () => ['organization', 'teams'] as const,
    positions: () => ['organization', 'positions'] as const,
    jobLevels: () => ['organization', 'job-levels'] as const,
    workLocations: () => ['organization', 'work-locations'] as const,
    payrollGroups: () => ['organization', 'payroll-groups'] as const,
  },
  attendance: {
    all: () => ['attendance'] as const,
    list: (filter: unknown) => ['attendance', 'list', filter] as const,
    one: (id: string) => ['attendance', 'one', id] as const,
    today: () => ['attendance', 'today'] as const,
  },
  corrections: {
    all: () => ['corrections'] as const,
    list: (filter: unknown) => ['corrections', 'list', filter] as const,
  },
  leave: {
    all: () => ['leave'] as const,
    list: (filter: unknown) => ['leave', 'list', filter] as const,
    dashboard: () => ['leave', 'dashboard'] as const,
  },
  overtime: {
    all: () => ['overtime'] as const,
    list: (filter: unknown) => ['overtime', 'list', filter] as const,
  },
  timesheets: {
    all: () => ['timesheets'] as const,
    list: (filter: unknown) => ['timesheets', 'list', filter] as const,
  },
  reports: {
    all: () => ['reports'] as const,
    workforce: () => ['reports', 'workforce'] as const,
    attendance: (range: unknown) => ['reports', 'attendance', range] as const,
    leave: () => ['reports', 'leave'] as const,
  },
  payroll: {
    all: () => ['payroll'] as const,
    runs: () => ['payroll', 'runs'] as const,
    run: (id: string) => ['payroll', 'run', id] as const,
    lines: (runId: string, filter: unknown) => ['payroll', 'lines', runId, filter] as const,
  },
  approvals: {
    all: () => ['approvals'] as const,
    list: (filter: unknown) => ['approvals', 'list', filter] as const,
  },
  recruitment: {
    all: () => ['recruitment'] as const,
    dashboard: () => ['recruitment', 'dashboard'] as const,
    requisitions: (filter: unknown) => ['recruitment', 'requisitions', filter] as const,
    candidates: (filter: unknown) => ['recruitment', 'candidates', filter] as const,
    candidate: (id: string) => ['recruitment', 'candidate', id] as const,
  },
  onboarding: {
    all: () => ['onboarding'] as const,
    list: (f: unknown) => ['onboarding', 'list', f] as const,
  },
  probation: {
    all: () => ['probation'] as const,
    list: (f: unknown) => ['probation', 'list', f] as const,
  },
  offboarding: {
    all: () => ['offboarding'] as const,
    list: (f: unknown) => ['offboarding', 'list', f] as const,
  },
  performance: {
    all: () => ['performance'] as const,
    cycles: () => ['performance', 'cycles'] as const,
    reviews: (f: unknown) => ['performance', 'reviews', f] as const,
  },
  learning: {
    all: () => ['learning'] as const,
    summary: () => ['learning', 'summary'] as const,
    courses: (f: unknown) => ['learning', 'courses', f] as const,
    records: (f: unknown) => ['learning', 'records', f] as const,
  },
  documents: {
    all: () => ['documents'] as const,
    list: (f: unknown) => ['documents', 'list', f] as const,
    summary: () => ['documents', 'summary'] as const,
  },
  policies: {
    all: () => ['policies'] as const,
    list: (f: unknown) => ['policies', 'list', f] as const,
  },
  announcements: {
    all: () => ['announcements'] as const,
    list: (f: unknown) => ['announcements', 'list', f] as const,
  },
  assets: {
    all: () => ['assets'] as const,
    list: (f: unknown) => ['assets', 'list', f] as const,
    summary: () => ['assets', 'summary'] as const,
  },
  relations: {
    all: () => ['relations'] as const,
    list: (f: unknown) => ['relations', 'list', f] as const,
  },
  benefits: {
    all: () => ['benefits'] as const,
    plans: (f: unknown) => ['benefits', 'plans', f] as const,
  },
  loans: { all: () => ['loans'] as const, list: (f: unknown) => ['loans', 'list', f] as const },
  compensation: {
    all: () => ['compensation'] as const,
    forEmployee: (id: string) => ['compensation', id] as const,
  },
  users: {
    all: () => ['users'] as const,
    list: (f: unknown) => ['users', 'list', f] as const,
    summary: () => ['users', 'summary'] as const,
  },
  roles: {
    all: () => ['roles'] as const,
    list: () => ['roles', 'list'] as const,
    one: (id: string) => ['roles', id] as const,
  },
  audit: {
    all: () => ['audit'] as const,
    list: (f: unknown) => ['audit', 'list', f] as const,
    forRecord: (id: string) => ['audit', 'record', id] as const,
  },
  notifications: {
    all: () => ['notifications'] as const,
    list: (f: unknown) => ['notifications', 'list', f] as const,
    unread: () => ['notifications', 'unread'] as const,
  },
  settings: { all: () => ['settings'] as const },
  search: (term: string) => ['search', term] as const,
};
